/**
 * A leitura do arquivo escolhido e a cópia da foto da carteirinha.
 *
 * Papel: aqui ficam `temFoto`, que separa a apólice com foto gravada — a única
 * que abre o `ModalDaCarteirinha` —, `lerFotoDaCarteirinha`, que transforma o
 * arquivo escolhido no seletor em data URL já validada por
 * `novaFotoDaCarteirinhaSchema`, e `copiarFotoComoPng`, que põe a foto na área
 * de transferência como `image/png`.
 *
 * Como usar: o `CampoFotoDaCarteirinha` e o `ModalDaCarteirinha` leem o arquivo
 * por `lerFotoDaCarteirinha`, e nenhum dos dois deverá chamar o `FileReader`
 * por conta própria — a mensagem de recusa e a extensão do nome do arquivo
 * passariam a sair de dois lugares, e um dia uma das telas aceitaria o que a
 * outra recusa.
 *
 * `copiarFotoComoPng` deverá ser chamada direto no manipulador do clique, sem
 * nenhum `await` antes dela: o Safari só aceita escrever na área de
 * transferência durante o gesto da pessoa, e por isso o `ClipboardItem` nasce
 * na hora com a Promise do PNG, que termina de ser convertido depois.
 */
import {
  EXTENSAO_DA_FOTO,
  type ExtensaoDaFotoDaCarteirinha,
  type FotoDaCarteirinha,
  type PlanoSaude,
  novaFotoDaCarteirinhaSchema,
} from '@concierge/shared';

/** Uma apólice que tem foto gravada — a única que abre o modal. */
export type PlanoComFoto = PlanoSaude & { foto: FotoDaCarteirinha };

/**
 * Confere se a apólice tem foto gravada.
 *
 * @param plano - A apólice da linha, ou a que o envio devolveu.
 * @returns Verdadeiro quando `foto` não é `null`, estreitando para `PlanoComFoto`.
 */
export function temFoto(plano: PlanoSaude): plano is PlanoComFoto {
  return plano.foto !== null;
}

/** O resultado de `lerFotoDaCarteirinha`: a imagem pronta ou o motivo da recusa. */
export type LeituraDaFoto =
  | { ok: true; dataUrl: string; extensao: ExtensaoDaFotoDaCarteirinha }
  | { ok: false; mensagem: string };

/**
 * A extensão de uma data URL que já passou em `novaFotoDaCarteirinhaSchema`.
 *
 * @param dataUrl - A imagem validada.
 * @returns `png` ou `webp` pelo tipo declarado, e `jpg` no restante — o
 *   schema só deixa passar os três.
 */
function extensaoDaDataUrl(dataUrl: string): ExtensaoDaFotoDaCarteirinha {
  if (dataUrl.startsWith('data:image/png;')) return EXTENSAO_DA_FOTO['image/png'];
  if (dataUrl.startsWith('data:image/webp;')) return EXTENSAO_DA_FOTO['image/webp'];
  return EXTENSAO_DA_FOTO['image/jpeg'];
}

/**
 * Lê o arquivo escolhido como data URL e o confere com o schema do envio.
 *
 * Quem chama são o campo da foto no formulário do plano e o modal da
 * carteirinha, logo depois da escolha. A Promise nunca rejeita: o arquivo que
 * não é JPEG, PNG nem WEBP, o grande demais e o que o navegador não conseguiu
 * ler voltam como `ok: false`, com a frase pronta para aparecer embaixo da
 * imagem.
 *
 * @param arquivo - O arquivo do seletor, ou o solto sobre a prévia.
 * @returns A data URL e a extensão do nome do arquivo, ou a mensagem da recusa.
 */
export function lerFotoDaCarteirinha(arquivo: File): Promise<LeituraDaFoto> {
  return new Promise((resolver) => {
    const leitor = new FileReader();
    leitor.onload = () => {
      if (typeof leitor.result !== 'string') {
        resolver({ ok: false, mensagem: 'Não foi possível ler o arquivo.' });
        return;
      }
      const validado = novaFotoDaCarteirinhaSchema.safeParse({ foto: leitor.result });
      if (!validado.success) {
        resolver({
          ok: false,
          mensagem: validado.error.issues[0]?.message ?? 'Não foi possível usar esta imagem.',
        });
        return;
      }
      resolver({
        ok: true,
        dataUrl: validado.data.foto,
        extensao: extensaoDaDataUrl(validado.data.foto),
      });
    };
    leitor.onerror = () => {
      resolver({ ok: false, mensagem: 'Não foi possível ler o arquivo.' });
    };
    leitor.readAsDataURL(arquivo);
  });
}

/**
 * Converte a imagem em PNG, desenhando-a num `<canvas>`.
 *
 * @param imagem - A foto como veio da API; a que já é PNG volta como está.
 * @returns O `Blob` em `image/png`.
 */
async function paraPng(imagem: Blob): Promise<Blob> {
  if (imagem.type === 'image/png') return imagem;

  const bitmap = await createImageBitmap(imagem);
  const quadro = document.createElement('canvas');
  quadro.width = bitmap.width;
  quadro.height = bitmap.height;
  const contexto = quadro.getContext('2d');
  if (contexto === null) throw new Error('O navegador não desenhou a imagem.');
  contexto.drawImage(bitmap, 0, 0);
  bitmap.close();

  return new Promise((resolver, rejeitar) => {
    quadro.toBlob((png) => {
      if (png === null) rejeitar(new Error('O navegador não converteu a imagem.'));
      else resolver(png);
    }, 'image/png');
  });
}

/**
 * Escreve a foto na área de transferência como `image/png`.
 *
 * Quem chama é o clique na imagem do `ModalDaCarteirinha`. Os navegadores só
 * aceitam PNG num `ClipboardItem`, e por isso a foto em JPEG ou WEBP é
 * convertida antes; a conversão corre dentro da Promise entregue ao
 * `ClipboardItem`, criado ainda no clique. Sem `navigator.clipboard.write` ou
 * sem `ClipboardItem` — o Firefox antigo, ou a página fora de HTTPS —, a
 * Promise rejeita sem tentar nada.
 *
 * @param imagem - A foto gravada, como `Blob`.
 * @returns A Promise da escrita, que rejeita em qualquer falha.
 */
export function copiarFotoComoPng(imagem: Blob): Promise<void> {
  // `navigator.clipboard` não existe fora de contexto seguro, ainda que o
  // tipo do DOM o declare sempre presente.
  const areaDeTransferencia: Clipboard | undefined = navigator.clipboard;
  if (
    typeof ClipboardItem === 'undefined' ||
    typeof areaDeTransferencia?.write !== 'function'
  ) {
    return Promise.reject(new Error('Área de transferência indisponível.'));
  }
  try {
    const item = new ClipboardItem({ 'image/png': paraPng(imagem) });
    return areaDeTransferencia.write([item]);
  } catch (erro) {
    // O navegador que não aceita Promise no `ClipboardItem` lança na criação.
    return Promise.reject(erro instanceof Error ? erro : new Error('Cópia recusada.'));
  }
}
